import { cleanPilotName, isServerAddress } from "../net/protocol";
import { cloneRules, type MatchRules, MODERN_RULES, normalizeRules } from "../sim/match-rules";
import { ACTIONS, DEFAULT_BINDINGS, type KeyBindings } from "./input/bindings";
import type { TouchMode } from "./input/touch";
import type { SpeedZoom } from "./render/camera";
import type { ResolutionMode } from "./resolution";

/**
 * Persisted user settings (localStorage). Every access is guarded: storage can be
 * unavailable (private mode, blocked cookies) and the game must still work; anything
 * malformed falls back to defaults field by field.
 */
export interface Settings {
	volume: number;
	muted: boolean;
	/** Keyboard bindings per local player (index 0 = blue, 1 = red). */
	bindings: KeyBindings[];
	/** Pixel density: auto (sharp until the device struggles), sharp, or standard (1×). */
	resolution: ResolutionMode;
	/** The rule set last used with "Custom" rules. */
	customRules: MatchRules;
	/** Screen shake and UI animation: follow the OS setting, or force reduced/full motion. */
	reducedMotion: MotionPreference;
	/** Colour-blind friendly "P1" / "P2" / "AI" tags under the ships. */
	pilotMarkers: boolean;
	/** The view slides towards where the ship flies (P10.1); off = the centred 2008 camera. */
	lookAhead: boolean;
	/** How far the view zooms out at speed: the 2008 curve or a wider one. */
	speedZoom: SpeedZoom;
	/** On-screen touch controls: on touch screens (auto), always or never. */
	touchControls: TouchMode;
	/** Online relay server (`host[:port]` or a ws/wss/http(s) URL); empty = this site. */
	onlineServer: string;
	/** Pilot names per local player (P11.8); "" = the ship colour. Player 1's goes online. */
	pilotNames: string[];
}

export type MotionPreference = "system" | "on" | "off";

const KEY = "gravity.settings.v1";
export const ONLINE_SERVER_MAX = 200;

export function defaultSettings(): Settings {
	return {
		volume: 0.6,
		muted: false,
		bindings: DEFAULT_BINDINGS.map(cloneBindings),
		resolution: "auto",
		customRules: cloneRules(MODERN_RULES),
		reducedMotion: "system",
		pilotMarkers: false,
		onlineServer: "",
		lookAhead: true,
		speedZoom: "classic",
		touchControls: "auto",
		pilotNames: ["", "", "", ""],
	};
}

export function cloneBindings(b: KeyBindings): KeyBindings {
	return {
		thrust: [...b.thrust],
		left: [...b.left],
		right: [...b.right],
		fireLaser: [...b.fireLaser],
		fireMissile: [...b.fireMissile],
	};
}

/** Validates untrusted stored data into Settings. */
export function parseSettings(raw: unknown): Settings {
	const defaults = defaultSettings();
	if (typeof raw !== "object" || raw === null) return defaults;
	const r = raw as Record<string, unknown>;
	return {
		volume:
			typeof r.volume === "number" && r.volume >= 0 && r.volume <= 1 ? r.volume : defaults.volume,
		muted: typeof r.muted === "boolean" ? r.muted : defaults.muted,
		resolution:
			r.resolution === "auto" || r.resolution === "sharp" || r.resolution === "standard"
				? r.resolution
				: r.hiDpi === false // saved before P10.13
					? "standard"
					: defaults.resolution,
		customRules: normalizeRules(r.customRules, defaults.customRules),
		reducedMotion:
			r.reducedMotion === "on" || r.reducedMotion === "off" || r.reducedMotion === "system"
				? r.reducedMotion
				: defaults.reducedMotion,
		pilotMarkers: typeof r.pilotMarkers === "boolean" ? r.pilotMarkers : defaults.pilotMarkers,
		touchControls:
			r.touchControls === "auto" || r.touchControls === "on" || r.touchControls === "off"
				? r.touchControls
				: defaults.touchControls,
		lookAhead: typeof r.lookAhead === "boolean" ? r.lookAhead : defaults.lookAhead,
		speedZoom:
			r.speedZoom === "classic" || r.speedZoom === "wide" ? r.speedZoom : defaults.speedZoom,
		onlineServer:
			typeof r.onlineServer === "string" &&
			r.onlineServer.length <= ONLINE_SERVER_MAX &&
			isServerAddress(r.onlineServer)
				? r.onlineServer.trim()
				: defaults.onlineServer,
		pilotNames: defaults.pilotNames.map((_, i) =>
			cleanPilotName(Array.isArray(r.pilotNames) ? r.pilotNames[i] : undefined),
		),
		bindings: defaults.bindings.map((fallback, i) =>
			parseBindings(Array.isArray(r.bindings) ? r.bindings[i] : undefined, fallback),
		),
	};
}

function parseBindings(raw: unknown, fallback: KeyBindings): KeyBindings {
	if (typeof raw !== "object" || raw === null) return fallback;
	const r = raw as Record<string, unknown>;
	const result = cloneBindings(fallback);
	for (const action of ACTIONS) {
		const codes = r[action];
		if (Array.isArray(codes) && codes.every((c) => typeof c === "string" && c.length > 0)) {
			result[action] = [...(codes as string[])];
		}
	}
	return result;
}

export function loadSettings(): Settings {
	try {
		const raw = window.localStorage.getItem(KEY);
		return raw ? parseSettings(JSON.parse(raw)) : defaultSettings();
	} catch {
		return defaultSettings();
	}
}

export function saveSettings(settings: Settings): void {
	try {
		window.localStorage.setItem(KEY, JSON.stringify(settings));
	} catch {
		// Storage unavailable: settings just won't persist.
	}
}
